import type { UserRole } from '../types'
import {
  gerarObrigacoesDosParticipantes,
  type ObrigacaoOperacional,
  type ParticipanteOperacao,
  type SeveridadeObrigacao,
} from './operacaoParticipantes'

export type TipoOperacaoNormativa =
  | 'aplicacao_agrotoxico_arp'
  | 'aplicacao_fertilizante_arp'
  | 'semeadura_arp'
  | 'mapeamento_monitoramento'
  | 'transporte_logistica'
  | 'outra'

export type EtapaWorkflowOperacao =
  | 'rascunho'
  | 'planejada'
  | 'documentacao_pendente'
  | 'documentacao_validada'
  | 'equipe_pendente'
  | 'equipe_confirmada'
  | 'pre_operacao_pendente'
  | 'aro_pendente'
  | 'liberada_para_operacao'
  | 'em_execucao'
  | 'pos_operacao_pendente'
  | 'finalizada'
  | 'relatorio_gerado'
  | 'conforme'
  | 'conforme_com_observacao'
  | 'nao_conforme'
  | 'bloqueada'
  | 'cancelada'

export type RegraTerritorialAplicavel = {
  uf: string
  municipio?: string | null
  distancia_minima_metros?: number | null
  exige_cadastro_estadual?: boolean
  exige_art?: boolean
  exige_rt?: boolean
  exige_caar?: boolean
  proibe_operacao?: boolean
  observacao?: string | null
  base_normativa?: string | null
}

export type DocumentoOperacional = {
  tipo: string
  nome: string
  status: 'valido' | 'vencido' | 'pendente' | 'nao_aplicavel'
  vinculadoA?: UserRole | 'operadora' | 'aeronave' | 'produto' | 'operacao'
}

export type EquipamentoOperacional = {
  id: string
  tipo: string
  nome?: string | null
  status?: string | null
  exige_checklist?: boolean
}

export type OperacaoNormativaContext = {
  operacaoId: string
  operadoraId: string
  tipoOperacao: TipoOperacaoNormativa
  uf?: string | null
  municipio?: string | null
  usaArp?: boolean
  envolveVoo?: boolean
  envolveProdutoExposicao?: boolean
  envolveAgrotoxico?: boolean
  exigeMeteorologia?: boolean
  participantes: ParticipanteOperacao[]
  equipamentos?: EquipamentoOperacional[]
  documentos?: DocumentoOperacional[]
  regrasTerritoriais?: RegraTerritorialAplicavel[]
}

export type BloqueioOperacional = {
  codigo: string
  titulo: string
  descricao: string
  severidade: SeveridadeObrigacao
  origem: 'documento' | 'equipe' | 'territorio' | 'equipamento' | 'checklist' | 'meteorologia' | 'produto'
}

export type ResultadoConformidadeOperacao = {
  operacaoId: string
  etapaSugerida: EtapaWorkflowOperacao
  obrigacoes: ObrigacaoOperacional[]
  bloqueios: BloqueioOperacional[]
  alertas: BloqueioOperacional[]
  podeIniciar: boolean
  statusResumo: 'conforme' | 'pendente' | 'bloqueada' | 'atencao'
}

type RegraBloqueio = {
  codigo: string
  titulo: string
  descricao: string | ((ctx: OperacaoNormativaContext) => string)
  origem: BloqueioOperacional['origem']
  quando: (ctx: OperacaoNormativaContext) => boolean
}

const TIPOS_OPERACAO_COM_CAAR = new Set<TipoOperacaoNormativa>([
  'aplicacao_agrotoxico_arp',
  'aplicacao_fertilizante_arp',
  'semeadura_arp',
])

const DOCUMENT_STATUS_INVALIDO = new Set<DocumentoOperacional['status']>(['pendente', 'vencido'])

function participantesAtivos(ctx: OperacaoNormativaContext) {
  return ctx.participantes.filter(p => p.ativo !== false)
}

function temParticipante(ctx: OperacaoNormativaContext, role: UserRole) {
  return participantesAtivos(ctx).some(p => p.papel === role)
}

function temPilotoOuOperadorAeronave(ctx: OperacaoNormativaContext) {
  return participantesAtivos(ctx).some(p => p.papel === 'piloto' || p.opera_aeronave)
}

function temCaarOuAplicadorHabilitado(ctx: OperacaoNormativaContext) {
  return participantesAtivos(ctx).some(p => p.papel === 'caar' || p.papel === 'aplicador')
}

function documentoValido(ctx: OperacaoNormativaContext, tipo: string) {
  return (ctx.documentos ?? []).some(doc => doc.tipo === tipo && doc.status === 'valido')
}

function documentoInvalido(ctx: OperacaoNormativaContext, tipo: string) {
  return (ctx.documentos ?? []).some(doc => doc.tipo === tipo && DOCUMENT_STATUS_INVALIDO.has(doc.status))
}

function algumaRegra(ctx: OperacaoNormativaContext, check: (regra: RegraTerritorialAplicavel) => boolean) {
  return (ctx.regrasTerritoriais ?? []).some(check)
}

function operacaoExigeCaar(ctx: OperacaoNormativaContext) {
  return !!ctx.usaArp && (
    TIPOS_OPERACAO_COM_CAAR.has(ctx.tipoOperacao) ||
    algumaRegra(ctx, regra => !!regra.exige_caar)
  )
}

function operacaoExigeRt(ctx: OperacaoNormativaContext) {
  return algumaRegra(ctx, regra => !!regra.exige_rt)
}

function operacaoExigeArt(ctx: OperacaoNormativaContext) {
  return algumaRegra(ctx, regra => !!regra.exige_art)
}

const REGRAS_BLOQUEIO: RegraBloqueio[] = [
  {
    codigo: 'EQUIPE_AUSENTE',
    titulo: 'Equipe não escalada',
    descricao: 'A operação precisa ter participantes vinculados antes de gerar ponto, checklist e relatório.',
    origem: 'equipe',
    quando: ctx => participantesAtivos(ctx).length === 0,
  },
  {
    codigo: 'PILOTO_AUSENTE',
    titulo: 'Piloto não escalado',
    descricao: 'Operação com voo precisa ter piloto ou operador de aeronave vinculado.',
    origem: 'equipe',
    quando: ctx => !!ctx.envolveVoo && !temPilotoOuOperadorAeronave(ctx),
  },
  {
    codigo: 'CAAR_AUSENTE',
    titulo: 'CAAR não escalado',
    descricao: 'Operação aeroagrícola remota precisa de CAAR/aplicador habilitado quando aplicável.',
    origem: 'equipe',
    quando: ctx => operacaoExigeCaar(ctx) && !temCaarOuAplicadorHabilitado(ctx),
  },
  {
    codigo: 'RT_AUSENTE',
    titulo: 'Responsável técnico não vinculado',
    descricao: 'A regra aplicável exige responsável técnico vinculado à operação.',
    origem: 'equipe',
    quando: ctx => operacaoExigeRt(ctx) && !temParticipante(ctx, 'responsavel_tecnico'),
  },
  {
    codigo: 'CADASTRO_ESTADUAL_PENDENTE',
    titulo: 'Cadastro estadual pendente',
    descricao: 'A regra territorial exige cadastro estadual válido para operar neste local.',
    origem: 'territorio',
    quando: ctx => algumaRegra(ctx, regra => !!regra.exige_cadastro_estadual) && !documentoValido(ctx, 'cadastro_estadual'),
  },
  {
    codigo: 'ANAC_SISANT_INVALIDO',
    titulo: 'Regularidade da aeronave pendente',
    descricao: 'A operação com ARP precisa preservar regularidade/documentação aeronáutica quando aplicável.',
    origem: 'documento',
    quando: ctx => !!ctx.usaArp && documentoInvalido(ctx, 'anac_sisant'),
  },
  {
    codigo: 'SARPAS_DECEA_PENDENTE',
    titulo: 'Acesso ao espaço aéreo pendente',
    descricao: 'Verifique a necessidade de SARPAS/DECEA e anexe evidência quando aplicável.',
    origem: 'documento',
    quando: ctx => !!ctx.envolveVoo && documentoInvalido(ctx, 'sarpras_decea'),
  },
  {
    codigo: 'DOCUMENTO_CAAR_INVALIDO',
    titulo: 'Documento CAAR pendente ou vencido',
    descricao: 'O participante CAAR/aplicador precisa estar com documento válido cadastrado.',
    origem: 'documento',
    quando: ctx => operacaoExigeCaar(ctx) && documentoInvalido(ctx, 'caar'),
  },
  {
    codigo: 'ART_PENDENTE',
    titulo: 'ART pendente',
    descricao: 'A regra aplicável exige ART válida para a operação.',
    origem: 'documento',
    quando: ctx => operacaoExigeArt(ctx) && !documentoValido(ctx, 'art'),
  },
]

function gerarBloqueios(ctx: OperacaoNormativaContext): BloqueioOperacional[] {
  const bloqueiosRegras = REGRAS_BLOQUEIO
    .filter(regra => regra.quando(ctx))
    .map<BloqueioOperacional>(regra => ({
      codigo: regra.codigo,
      titulo: regra.titulo,
      descricao: typeof regra.descricao === 'function' ? regra.descricao(ctx) : regra.descricao,
      severidade: 'bloqueante',
      origem: regra.origem,
    }))

  const bloqueiosTerritorio = (ctx.regrasTerritoriais ?? [])
    .filter(regra => regra.proibe_operacao)
    .map<BloqueioOperacional>(regra => ({
      codigo: 'OPERACAO_PROIBIDA_TERRITORIO',
      titulo: 'Operação bloqueada por regra territorial',
      descricao: regra.observacao || 'Há regra estadual ou municipal que impede a operação neste local.',
      severidade: 'bloqueante',
      origem: 'territorio',
    }))

  return [...bloqueiosRegras, ...bloqueiosTerritorio]
}

function gerarObrigacoesGerais(ctx: OperacaoNormativaContext): ObrigacaoOperacional[] {
  if (!ctx.exigeMeteorologia && !ctx.envolveAgrotoxico && !ctx.envolveVoo) return []

  return [{
    tipo: 'meteorologia',
    etapa: 'pre_operacao',
    titulo: 'Registrar meteorologia da operação',
    descricao: 'Registrar vento, direção do vento, temperatura, umidade e origem do dado antes da liberação.',
    severidade: 'bloqueante',
    papel: 'admin',
    motivo: 'Operações aeroagrícolas dependem de avaliação meteorológica para segurança e rastreabilidade.',
  }]
}

function gerarAlertas(obrigacoes: ObrigacaoOperacional[]) {
  return obrigacoes
    .filter(item => item.severidade === 'recomendada')
    .map<BloqueioOperacional>(item => ({
      codigo: `ALERTA_${item.tipo.toUpperCase()}`,
      titulo: item.titulo,
      descricao: item.descricao,
      severidade: 'recomendada',
      origem: 'checklist',
    }))
}

function resolverEtapaSugerida(params: {
  obrigacoes: ObrigacaoOperacional[]
  bloqueiosCriticos: BloqueioOperacional[]
}): EtapaWorkflowOperacao {
  const { obrigacoes, bloqueiosCriticos } = params

  if (bloqueiosCriticos.length > 0) return 'bloqueada'
  if (obrigacoes.some(item => item.etapa === 'antes_voo' && item.severidade === 'bloqueante')) return 'aro_pendente'
  if (obrigacoes.some(item => item.etapa === 'pre_operacao' && item.severidade === 'bloqueante')) return 'pre_operacao_pendente'
  return 'liberada_para_operacao'
}

function resolverStatusResumo(params: {
  obrigacoes: ObrigacaoOperacional[]
  bloqueiosCriticos: BloqueioOperacional[]
  alertas: BloqueioOperacional[]
}): ResultadoConformidadeOperacao['statusResumo'] {
  if (params.bloqueiosCriticos.length > 0) return 'bloqueada'
  if (params.obrigacoes.length > 0) return 'pendente'
  return params.alertas.length > 0 ? 'atencao' : 'conforme'
}

export function avaliarOperacaoNormativa(ctx: OperacaoNormativaContext): ResultadoConformidadeOperacao {
  const obrigacoes = [
    ...gerarObrigacoesDosParticipantes(ctx.participantes),
    ...gerarObrigacoesGerais(ctx),
  ]

  const bloqueios = gerarBloqueios(ctx)
  const alertas = gerarAlertas(obrigacoes)
  const bloqueiosCriticos = bloqueios.filter(item => item.severidade === 'bloqueante')

  return {
    operacaoId: ctx.operacaoId,
    etapaSugerida: resolverEtapaSugerida({ obrigacoes, bloqueiosCriticos }),
    obrigacoes,
    bloqueios,
    alertas,
    podeIniciar: bloqueiosCriticos.length === 0,
    statusResumo: resolverStatusResumo({ obrigacoes, bloqueiosCriticos, alertas }),
  }
}
